// The light markup Claire writes in Airtable long-text fields (FAQ answers,
// expert article content). Deliberately tiny — four things, all explained in
// the field descriptions in Airtable:
//
//   blank line          → new paragraph
//   "- item"            → bullet list   ("1. item" → numbered list)
//   "## Heading"        → subheading
//   [text](url)         → link (a leading "/" means a page on this site)
//   **text**            → bold
//
// Not markdown: no nesting, no images, no raw HTML, so nothing pasted in can
// break the page or inject anything.

const INLINE = /(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)/g;

/**
 * One line of text → the segment array ContentPage's InlineText understands:
 * strings, {label, to} / {label, href} for links, {bold} for emphasis.
 * Returns a plain string when there's nothing inline, which InlineText also takes.
 */
export function parseInline(line) {
  const out = [];
  let last = 0;
  for (const m of String(line).matchAll(INLINE)) {
    if (m.index > last) out.push(line.slice(last, m.index));
    if (m[1]) {
      const url = m[3];
      out.push(url.startsWith('/') ? { label: m[2], to: url } : { label: m[2], href: url });
    } else {
      out.push({ bold: m[5] });
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out.length === 1 && typeof out[0] === 'string' ? out[0] : out;
}

/**
 * Whole field → blocks: {type:'p', text}, {type:'h', text}, {type:'list', ordered, items}
 * where text/items are InlineText segment values.
 */
export function parseLightMarkup(text) {
  const blocks = [];
  const chunks = String(text || '').replace(/\r\n/g, '\n').split(/\n\s*\n/);
  for (const chunk of chunks) {
    const lines = chunk.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    const bullet = lines.every((l) => /^- /.test(l));
    const numbered = lines.every((l) => /^\d+\. /.test(l));
    if (bullet || numbered) {
      blocks.push({
        type: 'list',
        ordered: numbered,
        items: lines.map((l) => parseInline(l.replace(/^(- |\d+\. )/, ''))),
      });
    } else if (lines.length === 1 && /^## /.test(lines[0])) {
      blocks.push({ type: 'h', text: parseInline(lines[0].replace(/^## /, '')) });
    } else {
      // Single newlines inside a chunk are soft wraps — join them.
      blocks.push({ type: 'p', text: parseInline(lines.join(' ')) });
    }
  }
  return blocks;
}

/**
 * Same field → the `a` parts array a ContentPage faq item takes, so answers
 * from Airtable render identically to the ones written in content/pages.
 * Headings become a bold paragraph; the FAQ answer style has no heading level.
 */
export function toFaqParts(text) {
  return parseLightMarkup(text).map((b) => {
    if (b.type === 'list') return { list: b.items, ordered: b.ordered };
    if (b.type === 'h') return [{ bold: typeof b.text === 'string' ? b.text : b.text.map((s) => (typeof s === 'string' ? s : s.label || s.bold || '')).join('') }];
    return b.text;
  });
}
